import cx from 'classnames';
import styles from './Checkbox.module.css';
import InputWithIcon from '../Inputs/InputWithIcon/InputWithIcon';
import Checkbox from './Checkbox';
import MultiCheckboxContainer from './MultiCheckboxContainer';
import React, { useEffect, useState } from 'react';
import { debounce, entriesIn, isEmpty } from 'lodash';
import { Tooltip } from '@salesforce/design-system-react';
import { Control, Controller } from 'react-hook-form';

interface Props {
	name?: string;
	control?: Control<any>;
	isShowTooltip?: boolean;
	entries: any;
	onEntryClick: (x, y) => void;
	getEntryValue?: (entry) => any;
	disabled?: boolean;
}

const ToolTipContainer = ({ children, disabled, isShowTooltip }) =>
	disabled && isShowTooltip ? (
		<Tooltip
			id="tooltip"
			align="bottom left"
			content={
				<span>
					This TPAID is already covered
					<br />
					by another wallet.
				</span>
			}
			triggerClassName={styles.toolTip}
		>
			{children}
		</Tooltip>
	) : (
		children
	);

function TAPIDCheckboxPortion({
	entries = [],
	onEntryClick,
	disabled = false,
	isShowTooltip,
}) {
	return (
		<>
			{entries.map((entry, i) => {
				const { id, tpa_id, checked, has_wallet } = entry;
				const _disabled = disabled || has_wallet;
				return (
					<ToolTipContainer
						key={id}
						disabled={has_wallet}
						isShowTooltip={isShowTooltip}
					>
						<div
							key={i}
							className={cx('slds-col', styles.entry, {
								[styles.disabledChkBox]: _disabled,
							})}
						>
							<Checkbox
								label={tpa_id}
								checked={checked}
								onChange={() => {
									onEntryClick(entry, !checked);
								}}
								disabled={_disabled}
							/>
						</div>
					</ToolTipContainer>
				);
			})}
		</>
	);
}

const TAPIDCheckbox: React.FC<Props> = ({
	name = '',
	control,
	isShowTooltip,
	entries,
	onEntryClick,
	disabled,
}) => {
	const halfIndex = Math.ceil(entries.length / 2) - 1;

	const [selected, setSelected] = useState(false);
	const [filteredEntries, setFilteredEntries] = useState<any | null>(entries);
	const [search, setSearch] = useState('');
	const handleSearchChange = (e) => setSearch(e.target.value);

	const disabledIfAllHasWallet =
		entries.filter((e) => e.has_wallet).length === entries.length;

	const handleSearch = debounce(() => {
		if (search === '') {
			setFilteredEntries(entries);
			return;
		}
		setFilteredEntries(
			entries.filter((tpa) =>
				tpa.tpa_id.toLowerCase().includes(search.trim().toLowerCase())
			)
		);
	}, 300);

	useEffect(() => {
		handleSearch();
	}, [search]);

	useEffect(() => {
		isEmpty(search) && setFilteredEntries(() => entries);
		if (
			entries.filter(({ checked }) => checked === false).length ===
			entries.length
		)
			setSelected(false);
	}, [entries]);

	useEffect(() => {
		setFilteredEntries((preFiltered) =>
			preFiltered.map((e) => {
				const match = entries.find(({ id }) => id === e.id);
				return match ? match : e;
			})
		);
	}, [entries]);

	const handleSelectAll = () => {
		setSelected(!selected);
		entries.forEach((e) => !e.has_wallet && onEntryClick(e, !selected));
	};

	const MainCheckbox = ({ field = { onChange: (v) => {} }, error }) => (
		<MultiCheckboxContainer
			className={cx(error && styles.errorCheckbox)}
			selectAll={selected}
			onSelectAll={() => {
				field.onChange(
					entries
						.map((e) => {
							e.checked = !selected;
							return e;
						})
						.filter((e) => e.checked)
				);
				handleSelectAll();
			}}
			selectAllLabel="Select All TPA IDs"
			disabled={disabled || disabledIfAllHasWallet}
		>
			<InputWithIcon
				placeholder="Search TPA IDs"
				value={search}
				onChange={handleSearchChange}
				icons={[
					{
						path: 'utility/search',
						isLeft: true,
					},
				]}
				disabled={disabled}
			/>
			<div className={cx('slds-grid', styles.listBody)}>
				{entries && entries.length > 0 ? (
					<>
						<div className="slds-grid_vertical slds-col slds-size_1-of-2">
							<TAPIDCheckboxPortion
								isShowTooltip={isShowTooltip}
								entries={filteredEntries}
								onEntryClick={(e, selected) => {
									const result = onEntryClick(e, selected);
									field.onChange(result);
								}}
								disabled={disabled}
							/>
						</div>
					</>
				) : (
					<div className={styles.noResult}>No Results Found</div>
				)}
			</div>
		</MultiCheckboxContainer>
	);

	return name && control ? (
		<Controller
			name={name}
			control={control}
			render={(props: any) => {
				const {
					field,
					fieldState: { error },
				} = props;
				return (
					<>
						{MainCheckbox({ field, error })}
						{error && (
							<div className={cx('slds-has-error')}>
								<div className={cx(styles.helper, 'slds-form-element__help')}>
									{error.message}
								</div>
							</div>
						)}
					</>
				);
			}}
		/>
	) : (
		MainCheckbox({ error: '' })
	);
};

export default TAPIDCheckbox;
